import { Button, Classes, Intent, Spinner } from '@blueprintjs/core';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';
import { emptyObject } from 'vet-bones/constants';

import { TEST_IDS } from 'src/constants/testIds';
import { useAddImportedModels } from 'src/containers/ProjectModelDialog/hooks/useAddImportedModels';
import { useAddLocalModel } from 'src/containers/ProjectModelDialog/hooks/useAddLocalModel';
import {
  AddModelType,
  useAddModelType,
} from 'src/containers/ProjectModelDialog/hooks/useAddModelTypeValue';
import { useAddUploadModel } from 'src/containers/ProjectModelDialog/hooks/useAddUploadModel';
import { useCloseModelDialog } from 'src/containers/ProjectModelDialog/hooks/useCloseModelDialog';
import { useImportModel } from 'src/containers/ProjectModelDialog/hooks/useImportModelValue';
import { useModelPanelStack } from 'src/containers/ProjectModelDialog/hooks/useModelPanelStackValue';
import { useResetModelPanels } from 'src/containers/ProjectModelDialog/hooks/useResetModelPanels';
import { useSubmitAddModel } from 'src/containers/ProjectModelDialog/hooks/useSubmitAddModel';
import { useUploadModel } from 'src/containers/ProjectModelDialog/hooks/useUploadModelValue';
import { connectionSelector } from 'src/selectors/connection';
import { isConnectedSelector } from 'src/selectors/isConnected';
import * as copy from 'src/templates/copy';
import { checkImportModelLimits } from 'src/utils/checkImportModelLimits';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectModelDialogFooter: React.VFC = () => {
  const { projectId } = useRouterParams();
  const { onClose } = useModelPanelStack();
  const { addModelType } = useAddModelType();
  const resetModelPanels = useResetModelPanels();
  const connection = useReduxSelector(connectionSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const isPendingAddModelToDb = useReduxSelector(
    (state) => state.databases.pendingAddModelToDatabase
  );

  const {
    isAddLocalModelEnabled,
    nameValidationError,
    namespaceValidationError,
  } = useAddLocalModel();
  const { isAddImportedModelsEnabled } = useAddImportedModels();
  const {
    isAddUploadModelEnabled,
    nameValidationError: uploadNameValidationError,
    namedGraphValidationError,
  } = useAddUploadModel();

  const {
    isModelDataLoading: isImportModelDataLoading,
    pendingModelData: pendingImportModelData,
    setIsModelDataLoading: setIsImportModelDataLoading,
    setPendingModelData: setPendingImportModelData,
  } = useImportModel();
  const {
    isModelDataLoading: isUploadModelDataLoading,
    pendingModelData: pendingUploadModelData,
    setIsModelDataLoading: setIsUploadModelDataLoading,
    setPendingModelData: setPendingUploadModelData,
  } = useUploadModel();

  const isOverLimitError = React.useMemo(() => {
    if (addModelType === AddModelType.LOCAL) {
      return '';
    }

    const isOverLimit =
      addModelType === AddModelType.UPLOAD
        ? checkImportModelLimits(
            pendingUploadModelData
              ? {
                  [pendingUploadModelData.modelName]: pendingUploadModelData,
                }
              : emptyObject
          )
        : checkImportModelLimits(pendingImportModelData);
    return isOverLimit
      ? copy.components.projectModelDialog.validation.largeModelLimit
      : '';
  }, [addModelType, pendingImportModelData, pendingUploadModelData]);

  const handleCloseDialog = useCloseModelDialog();
  const handleBack = React.useCallback(() => {
    setPendingImportModelData({});
    setPendingUploadModelData(null);
    setIsImportModelDataLoading(false);
    setIsUploadModelDataLoading(false);

    if (addModelType) {
      onClose();
      resetModelPanels();
    } else {
      handleCloseDialog();
    }
  }, [
    addModelType,
    handleCloseDialog,
    onClose,
    resetModelPanels,
    setIsImportModelDataLoading,
    setIsUploadModelDataLoading,
    setPendingImportModelData,
    setPendingUploadModelData,
  ]);

  const handleSubmit = useSubmitAddModel();

  let isBackDisabled = false;
  let isSubmitDisabled = false;
  let validationError = '';
  if (addModelType === AddModelType.IMPORT) {
    isSubmitDisabled =
      !projectId ||
      !connection ||
      !isConnected ||
      !isAddImportedModelsEnabled ||
      isImportModelDataLoading;
    isBackDisabled = isImportModelDataLoading;
    validationError = isOverLimitError;
  } else if (addModelType === AddModelType.LOCAL) {
    validationError = nameValidationError || namespaceValidationError;
    isSubmitDisabled =
      !projectId || !isAddLocalModelEnabled || Boolean(validationError);
  } else if (addModelType === AddModelType.UPLOAD) {
    validationError = uploadNameValidationError || namedGraphValidationError;
    isBackDisabled = isPendingAddModelToDb || isUploadModelDataLoading;
    isSubmitDisabled =
      !projectId ||
      !connection ||
      !isConnected ||
      !isAddUploadModelEnabled ||
      (!pendingUploadModelData && Boolean(validationError));
  }

  return (
    <div className={Classes.DIALOG_FOOTER}>
      <div className={Classes.DIALOG_FOOTER_ACTIONS}>
        {addModelType ? (
          <>
            <Button
              data-testid={TEST_IDS.projectModelDialog.backButton}
              disabled={isBackDisabled}
              intent={Intent.PRIMARY}
              onClick={handleBack}
              outlined
              text={copy.components.projectModelDialog.backButton}
            />
            <Tooltip content={validationError} disabled={!validationError} fill>
              <Button
                data-testid={TEST_IDS.projectModelDialog.submitButton}
                disabled={isSubmitDisabled}
                intent={Intent.PRIMARY}
                onClick={handleSubmit}
                text={
                  isImportModelDataLoading ||
                  isUploadModelDataLoading ||
                  isPendingAddModelToDb ? (
                    <Spinner size={16} />
                  ) : (
                    copy.components.projectModelDialog.addButton
                  )
                }
              />
            </Tooltip>
          </>
        ) : null}
      </div>
    </div>
  );
};
